import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, Box, Pagination } from "@mui/material";
import commonStyles from "../../styles/Common.module.css";
import styles from "./styles/Filing.module.css";

const BASE_URL = "https://api.sovrenn.com";

const Filing = () => {
    const [filings, setFilings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const pageSize = 50;

    const [source, setSource] = useState("nse");
    const [allCompanies, setAllCompanies] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);

    const searchRef = useRef(null);

    // Fetch companies whenever source changes, and reset selection
    useEffect(() => {
        setSelectedCompany(null);
        setSearchQuery("");
        setSuggestions([]);
        setPage(1);

        const fetchCompanies = async () => {
            try {
                const res = await fetch(`${BASE_URL}/corporate-filings/companies?source=${source}`, {
                    headers: { "Authorization": "Bearer " + localStorage.getItem("sov_user") }
                });
                const result = await res.json();
                if (result.success) {
                    setAllCompanies(result.data);
                }
            } catch (err) {
                console.error(err);
            }
        };
        fetchCompanies();
    }, [source]);

    // Fetch filings whenever page, source or selectedCompany changes
    useEffect(() => {
        const fetchFilings = async () => {
            setIsLoading(true);
            try {
                let url = `${BASE_URL}/corporate-filings?source=${source}&page=${page}&page_size=${pageSize}`;

                if (selectedCompany) {
                    if (source === "nse") {
                        if (selectedCompany.isin) {
                            url += `&isin=${selectedCompany.isin}`;
                        } else {
                            url += `&symbol=${selectedCompany.symbol}`;
                        }
                    } else {
                        if (selectedCompany.scrip) {
                            url += `&scrip=${selectedCompany.scrip}`;
                        } else {
                            url += `&symbol=${selectedCompany.symbol}`;
                        }
                    }
                }

                const res = await fetch(url, {
                    headers: { "Authorization": "Bearer " + localStorage.getItem("sov_user") }
                });
                const result = await res.json();
                if (result.success) {
                    setFilings(result.data);
                    setTotalPages(result.pagination.total_pages);
                    setTotalDocuments(result.pagination.total_documents);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFilings();
    }, [page, source, selectedCompany]);

    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (query.trim().length === 0) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }

        const filtered = allCompanies
            .filter((c) =>
                c.companyName?.toLowerCase().includes(query.toLowerCase()) ||
                c.symbol?.toLowerCase().includes(query.toLowerCase()) ||
                String(c.scrip || "").includes(query)
            )
            .slice(0, 10);

        setSuggestions(filtered);
        setShowDropdown(true);
    };

    const handleSelectCompany = (company) => {
        setSelectedCompany(company);
        setSearchQuery(company.companyName);
        setShowDropdown(false);
        setPage(1);
    };

    const handleClearSearch = () => {
        setSelectedCompany(null);
        setSearchQuery("");
        setSuggestions([]);
        setShowDropdown(false);
        setPage(1);
    };

    const handleSourceChange = (e) => {
        setSource(e.target.value);
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className={commonStyles.MainContainer} style={{ width: "90%", maxWidth: "1300px" }}>
            <div className={commonStyles.headerDiv} style={{ alignItems: "flex-start" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <h1>Corporate Filings</h1>
                        <select value={source} onChange={handleSourceChange} className={styles.sourceSelect}>
                            <option value="nse">NSE</option>
                            <option value="bse">BSE</option>
                        </select>
                    </div>
                  
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {/* Search Bar */}
                    <div ref={searchRef} className={styles.searchWrapper}>
                        <div className={styles.searchInputRow}>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search company..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                            />
                            {searchQuery && (
                                <button className={styles.clearBtn} onClick={handleClearSearch}>✕</button>
                            )}
                        </div>

                        {showDropdown && suggestions.length > 0 && (
                            <div className={styles.dropdown}>
                                {suggestions.map((company, ind) => (
                                    <div
                                        key={company.isin || company.scrip || ind}
                                        className={styles.dropdownItem}
                                        onClick={() => handleSelectCompany(company)}
                                    >
                                        <span className={styles.companyName}>{company.companyName}</span>
                                       
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                
            </div>

            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", margin: "100px auto", width: 100 }}>
                    <CircularProgress />
                </Box>
            ) : filings.length === 0 ? (
                <p style={{ textAlign: "center", color: "#999", marginTop: "60px" }}>No filings found.</p>
            ) : (
                <>
                    <div className={styles.cardsContainer}>
                       
                      <p style={{ color: "#666", marginTop: "4px" }}>
                        {totalDocuments} filing{totalDocuments !== 1 ? "s" : ""}
                        {selectedCompany ? <> for <strong>{selectedCompany.companyName}</strong></> : ""}
                    </p>
                
                        {filings.map((filing, ind) => (
                            <div key={filing._id || ind} className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <span className={styles.cardCompany}>{filing.companyName || "-"}</span>
                                    <span className={styles.cardDate}>
                                        {filing.date ? new Date(filing.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                                    </span>
                                </div>
                                <p className={styles.cardDescription}>{filing.description || "-"}</p>
                                <div className={styles.cardFooter}>
                                    {filing.attachment_type && (
                                        <span className={styles.attachmentType}>{filing.attachment_type.toUpperCase()}</span>
                                    )}
                                    <div className={styles.cardLinks}>
                                        {filing?.localLink && (
                                            <a
                                                href={filing?.attachment_type === "pdf" ? `https://docs.google.com/viewer?url=${encodeURIComponent(filing?.localLink)}` : filing.localLink}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={styles.viewLink}
                                            >
                                                CDN File
                                            </a>
                                        )}
                                        {filing?.pdfLink && (
                                            <a
                                                href={filing.pdfLink}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={styles.viewLinkSecondary}
                                            >
                                                Original File
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, val) => setPage(val)}
                        color="primary"
                        style={{ marginTop: "24px" }}
                    />
                </>
            )}
        </div>
    );
};

export default Filing;
